import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { cellCenter, gearAt, gearSlotCell, golemOf, type BattleState, type GearRules } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** Grillo's head: a round lamp for an eye in a bronze dome. */
const GRILLO = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 11.5V8a5 5 0 0 1 10 0v3.5z" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="8.2" r="2" fill="currentColor"/><path d="M2 13h12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** A stone falling, its shadow under it. */
const STONE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 1.8l3.4.6 1.2 3-2.4 2.2-3.2-1z" fill="currentColor"/><ellipse cx="8" cy="13" rx="5" ry="1.6" fill="currentColor" opacity="0.55"/><path d="M7.8 8.6v2.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-dasharray="1 1.2"/></svg>';
/** An iron grille. */
const GRILLE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 2.5h11M2.5 13.5h11M4.5 2.5v11M8 2.5v11M11.5 2.5v11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** A golem's lamp. */
const LAMP = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="4" y="3" width="8" height="9" rx="2" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="7.5" r="2.1" fill="currentColor"/><path d="M5 14h6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** A gear. */
const GEAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.6l1.2 1.8 2.1-.6.4 2.2 2.1.8-.9 2 .9 2-2.1.8-.4 2.2-2.1-.6L8 14.4l-1.2-1.8-2.1.6-.4-2.2-2.1-.8.9-2-.9-2 2.1-.8.4-2.2 2.1.6z" fill="currentColor"/><circle cx="8" cy="8" r="2.1" fill="#0c0e12"/></svg>';
/** The water wheel. */
const WHEEL = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 2.4v11.2M2.4 8h11.2M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1"/><circle cx="8" cy="8" r="1.4" fill="currentColor"/></svg>';
/** The lift's drum, its chain going up. */
const DRUM = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="7" width="10" height="6" rx="1.4" fill="currentColor"/><path d="M8 1.5v5.5" stroke="currentColor" stroke-width="1.4" stroke-dasharray="1.6 1"/></svg>';
/** A boiler. */
const BOILER = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="4" y="4" width="8" height="10" rx="3" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M6.5 1.6c.6.6.6 1.4 0 2M9.5 1.6c.6.6.6 1.4 0 2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/><circle cx="8" cy="9" r="1.6" fill="currentColor"/></svg>';

/** The way a turning axle goes, by the sim's sign (+1 the wheel's way). */
const turnArrow = (dir: number): string => (dir > 0 ? '↻' : '↺');

type Mark = { el: HTMLButtonElement; teeth: HTMLElement; state: HTMLElement; slot: number; point: THREE.Vector3 };

/**
 * Nadd's workshop on screen (docs/sendas/artifice.md, chapter 1), inside a path's own plate (game/senda-hud.ts) and
 * over the board; a phone's plate kept under about a quarter of the screen:
 *
 * - the goat path (`cornice`): what Grillo is doing (on ahead, waiting for the hero, short of a stone's shadow, down and
 *   crawling to the next rest, getting up), his falls of two, and «¡Piedras!» red while a stone is coming.
 * - the sleeping workshop (`workshop`): the hall of five, the grille below it (down while one of its golems stands)
 *   and the golems awake; «¡Caldera abierta!» a moment when a boiler opens.
 * - the wheel and the belt (`gears`, the test): the wheel's speed and way, what the drum asks and what it does now (red
 *   while wrong, white when right, «trabado» when it jams), what is in the hero's hands, the next golem and the jams; the
 *   Overseer's two boilers once it is up. Over the board, a mark on each axle and each gear lying about: its teeth, and
 *   on a turning axle its way and speed in red, «Rueda» and «Tambor» at the two ends. A tap on a mark sends the hero
 *   there: what is in its hands and what is there change places.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class TallerHud {
  readonly panel: HTMLElement;
  private kind: 'cornice' | 'workshop' | 'gears';
  private v = new THREE.Vector3();
  private key = '';
  // The goat path.
  private grilloRow = h('div.bh-row.tl-grillo');
  private grilloText = h('span.al-wave');
  private downPips: HTMLElement[] = [];
  private stoneRow = h('div.bh-row.al-waverow.tl-stone');
  private stoneText = h('span.al-wave');
  // The workshop.
  private hallRow = h('div.bh-row.tl-hall');
  private hallText = h('span.al-wave');
  private grilleRow = h('div.bh-row.al-waverow.tl-grille');
  private grilleText = h('span.al-wave');
  private boilerUntil = 0;
  // The gear train.
  private wheelRow = h('div.bh-row.tl-wheel');
  private drumRow = h('div.bh-row.tl-drum');
  private drumWant = h('b.tl-want');
  private drumNow = h('span.tl-now');
  private handRow = h('div.bh-row.tl-hand');
  private handText = h('span.al-wave');
  private golemRow = h('div.bh-row.al-waverow.tl-golem');
  private golemText = h('span.al-wave');
  private bossRow = h('div.bh-row.tl-boss');
  private bossPips: HTMLElement[] = [];
  private marks: Mark[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const sr = s.rules?.senda;
    this.kind = sr?.gears ? 'gears' : sr?.workshop ? 'workshop' : 'cornice';
    const rows: HTMLElement[] = [];
    if (this.kind === 'cornice') {
      this.downPips = Array.from({ length: sr?.cornice?.downs ?? 2 }, () => h('i'));
      this.grilloRow.append(h('span.bh-ico', { html: GRILLO }), h('span.bh-label', {}, t('senda.taller.grillo')), this.grilloText, h('span.al-pips.tl-downs', { title: t('senda.taller.downs') }, ...this.downPips));
      this.stoneRow.append(h('span.bh-ico', { html: STONE }), this.stoneText);
      rows.push(this.grilloRow, this.stoneRow);
    } else if (this.kind === 'workshop') {
      this.hallRow.append(h('span.bh-ico', { html: LAMP }), this.hallText);
      this.grilleRow.append(h('span.bh-ico', { html: GRILLE }), this.grilleText);
      rows.push(this.hallRow, this.grilleRow);
    } else if (sr?.gears) {
      const r = sr.gears;
      this.wheelRow.append(h('span.bh-ico', { html: WHEEL }), h('span.bh-label', {}, t('senda.taller.wheel')), h('b.tl-speed', {}, `${r.wheel} ${turnArrow(1)}`));
      this.drumRow.append(h('span.bh-ico', { html: DRUM }), h('span.bh-label', {}, t('senda.taller.drum')), this.drumWant, this.drumNow);
      this.drumWant.textContent = t('senda.taller.asks', { n: r.want, way: turnArrow(-1) });
      this.handRow.append(h('span.bh-ico', { html: GEAR }), this.handText);
      this.golemRow.append(h('span.bh-ico', { html: LAMP }), this.golemText);
      this.bossPips = [h('i'), h('i')];
      this.bossRow.append(h('span.bh-ico', { html: BOILER }), h('span.bh-label', {}, t('senda.taller.boss')), h('span.al-pips.tl-boilers', {}, ...this.bossPips));
      this.bossRow.hidden = true;
      rows.push(this.wheelRow, this.drumRow, this.handRow, this.golemRow, this.bossRow);
      this.buildMarks(s, r);
    }
    this.panel = h(`div.bh.tl-panel.tl-${this.kind}`, {}, ...rows);
  }

  /** A mark on each axle and on each lying spot. */
  private buildMarks(s: BattleState, r: GearRules): void {
    const R = s.map.radius;
    const n = r.axles.length + r.pieces.length;
    for (let slot = 0; slot < n; slot++) {
      const c = cellCenter(R, gearSlotCell(r, slot));
      const axle = slot < r.axles.length;
      const teeth = h('b.tl-teeth');
      const state = h('small.al-state.tl-state');
      const name = slot === 0 ? t('senda.taller.wheel') : slot === 1 ? t('senda.taller.drum') : '';
      const el = h(
        `button.al-stone.tl-slot${axle ? '.axle' : '.lying'}${slot === 0 ? '.wheel' : slot === 1 ? '.drum' : ''}`,
        { type: 'button', 'data-test': `gear-slot-${slot}`, 'aria-label': t(axle ? 'senda.taller.axleHint' : 'senda.taller.gearHint'), title: t(axle ? 'senda.taller.axleHint' : 'senda.taller.gearHint'), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(slot)) },
        h('span.al-ring'),
        teeth,
        name ? h('b.mn-name.tl-name', {}, name) : '',
        state,
      ) as HTMLButtonElement;
      this.board.append(el);
      this.marks.push({ el, teeth, state, slot, point: simToWorld(c.x, c.y, new THREE.Vector3(), 0.25) });
    }
  }

  private updateCornice(s: BattleState): void {
    const r = s.rules?.senda?.cornice;
    const w = s.senda?.cornice;
    const g = s.senda?.npcs.find((n) => n.id === 'grillo');
    if (!r || !w || !g) return;
    const h0 = s.heroes.find((x) => x.downTicks === 0);
    const far = !!h0 && (Math.abs(h0.x - g.x) + Math.abs(h0.y - g.y)) * 10 > r.wait * 13;
    const up = w.rise >= 0 ? Math.max(0, Math.ceil((w.rise - s.tick) / TICK_HZ)) : -1;
    const text =
      w.knock >= 0
        ? t('senda.taller.knock')
        : w.downs >= r.downs
          ? t('senda.taller.gone')
          : w.crawl >= 0
            ? up >= 0
              ? t('senda.taller.rise', { s: up })
              : t('senda.taller.crawl')
            : far
              ? t('senda.taller.waits')
              : !g.moving && w.stones.some((st) => Math.abs(st.x - g.x) + Math.abs(st.y - g.y) < 2400)
                ? t('senda.taller.stops')
                : t('senda.taller.ahead');
    if (this.grilloText.textContent !== text) this.grilloText.textContent = text;
    this.grilloRow.classList.toggle('warn', w.crawl >= 0 || far);
    this.downPips.forEach((p, i) => p.classList.toggle('on', i < w.downs));
    // A stone coming down near the hero or Grillo (within two hexes): red, with its count; else the nests above.
    let next = -1;
    for (const st of w.stones) {
      const near = [h0, g].some((o) => o && Math.abs(o.x - st.x) + Math.abs(o.y - st.y) < 2600);
      if (near && (next < 0 || st.t - s.tick < next)) next = Math.max(0, st.t - s.tick);
    }
    const coming = next >= 0;
    const st = coming ? t('senda.taller.stones', { s: Math.max(1, Math.ceil(next / TICK_HZ)) }) : t('senda.taller.calm');
    if (this.stoneText.textContent !== st) this.stoneText.textContent = st;
    this.stoneRow.classList.toggle('warn', coming);
  }

  private updateWorkshop(s: BattleState): void {
    const r = s.rules?.senda?.workshop;
    const w = s.senda?.workshop;
    if (!r || !w) return;
    for (const e of s._events ?? []) if (e.t === 'senda' && e.kind === 'boiler') this.boilerUntil = performance.now() + 1800;
    let up = 0;
    for (const g of s.senda?.golem?.list ?? []) if (g.mode === 1 || g.mode === 2) up++;
    const hall = t('senda.taller.hall', { n: w.hall + 1, of: r.halls.length });
    const awake = up ? t(up === 1 ? 'senda.taller.awakeOne' : 'senda.taller.awake', { n: up }) : t('senda.taller.asleep');
    const text = `${hall} · ${awake}`;
    if (this.hallText.textContent !== text) this.hallText.textContent = text;
    const shut = !!w.shut[w.hall];
    const boiler = performance.now() < this.boilerUntil;
    const gt = boiler ? t('senda.taller.boilerOpen') : shut ? t('senda.taller.grilleDown') : w.hall >= r.halls.length - 1 ? t('senda.taller.lift') : t('senda.taller.grilleUp');
    if (this.grilleText.textContent !== gt) this.grilleText.textContent = gt;
    this.grilleRow.classList.toggle('warn', shut && !boiler);
    this.grilleRow.classList.toggle('good', boiler);
  }

  private updateGears(s: BattleState): void {
    const r = s.rules?.senda?.gears;
    const w = s.senda?.gears;
    if (!r || !w) return;
    const drumDir = w.dir[1] ?? 0;
    const drumSpeed = w.speed[1] ?? 0;
    const right = w.solved >= 0 || w.belt >= 0;
    const now = w.jam >= 0 ? t('senda.taller.jammed') : drumSpeed ? t('senda.taller.now', { n: drumSpeed, way: turnArrow(drumDir) }) : t('senda.taller.still');
    if (this.drumNow.textContent !== now) this.drumNow.textContent = now;
    this.drumRow.classList.toggle('right', right);
    this.drumRow.classList.toggle('wrong', !right && (drumSpeed > 0 || w.jam >= 0));
    const hand = w.hand >= 0 ? t('senda.taller.inHand', { n: r.pieces[w.hand] ?? 0 }) : w.belt >= 0 ? t('senda.taller.belt') : t('senda.taller.handFree');
    if (this.handText.textContent !== hand) this.handText.textContent = hand;
    const left = Math.max(0, Math.ceil((w.next - s.waveT) / TICK_HZ));
    const golem = w.down >= 0 ? t('senda.taller.bossOut') : t('senda.taller.golemIn', { s: left }) + (w.errors ? ` · ${t(w.errors === 1 ? 'senda.taller.jamOne' : 'senda.taller.jams', { n: w.errors })}` : '');
    if (this.golemText.textContent !== golem) this.golemText.textContent = golem;
    this.golemRow.classList.toggle('warn', w.down < 0 && left <= 5);
    // The Overseer's boilers once it is up.
    const boss = w.belt >= 0 ? golemOf(s, w.boss) : undefined;
    this.bossRow.hidden = !boss;
    if (boss) boss.open.forEach((o, i) => this.bossPips[i]?.classList.toggle('on', o > 0 || w.down >= 0));
    // The marks: their teeth, their way and speed.
    const key = `${w.on.join(',')}|${w.hand}|${w.dir.join('')}|${w.jam}|${w.belt}|${w.job}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        const k = gearAt(w, m.slot);
        const teeth = k >= 0 ? String(r.pieces[k]) : '';
        if (m.teeth.textContent !== teeth) m.teeth.textContent = teeth;
        const axle = m.slot < r.axles.length;
        const dir = axle ? (w.dir[m.slot] ?? 0) : 0;
        const state = axle && k >= 0 ? (w.jam === m.slot ? t('senda.taller.jamMark') : dir ? `${turnArrow(dir)} ${w.speed[m.slot] ?? 0}` : t('senda.taller.stillMark')) : m.slot === 1 && k < 0 ? t('senda.taller.asks', { n: r.want, way: turnArrow(-1) }) : '';
        if (m.state.textContent !== state) {
          m.state.textContent = state;
          remeasureMark(m.el);
        }
        m.el.classList.toggle('full', k >= 0);
        m.el.classList.toggle('turning', dir !== 0);
        m.el.classList.toggle('jam', w.jam === m.slot);
        m.el.classList.toggle('t12', k >= 0 && r.pieces[k] === 12);
        m.el.classList.toggle('t24', k >= 0 && r.pieces[k] === 24);
        m.el.classList.toggle('t36', k >= 0 && r.pieces[k] === 36);
        // A lying spot with nothing on it shows only with a gear in the hands (somewhere to put it down).
        m.el.classList.toggle('gone', w.belt >= 0 || (!axle && k < 0 && w.hand < 0));
      }
    }
    for (const m of this.marks) {
      const sent = w.job === m.slot;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.stand)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'cornice') this.updateCornice(s);
    else if (this.kind === 'workshop') this.updateWorkshop(s);
    else this.updateGears(s);
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
